import { Account } from '@/domain/usecases'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from '@tanstack/react-query'
import { defer, LoaderFunctionArgs } from 'react-router-dom'
import { executeGuard, Guard, GuardContext } from './guards'
import { queryClient } from '@/infra/http'
import { FeatureFlagClient } from '@/infra/feature-flag'

// ============================================
// TYPES
// ============================================

export type LoaderContext = {
  account: Account.Model
  queryClient: QueryClient
  ldClient?: LDClient
  params?: LoaderFunctionArgs['params']
  request?: LoaderFunctionArgs['request']
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DataFunction = (ctx: LoaderContext) => Promise<any> | any

// ============================================
// LOADER CREATION
// ============================================

/**
 * Creates a loader function with optional guard checks
 *
 * @param guard - Optional guard function to check before loading data
 * @param dataFn - Function to load data (can return Promise or plain value)
 * @returns Loader function compatible with React Router
 *
 * @example
 * // Simple loader without guard
 * createLoader(null, async ({ queryClient }) => {
 *   const data = await fetchData(queryClient)
 *   return { data }
 * })
 *
 * @example
 * // Loader with guard
 * createLoader(
 *   guards.companyManager,
 *   async ({ queryClient }) => {
 *     const data = await fetchDashboard(queryClient)
 *     return { data }
 *   }
 * )
 */
export function createLoader(guard: Guard | null, dataFn: DataFunction | null) {
  return async (args: LoaderFunctionArgs, ctx: LoaderContext) => {
    // Execute guard if provided
    if (guard) {
      const guardContext: GuardContext = {
        account: ctx.account,
        ldClient: ctx.ldClient
      }
      const guardResult = executeGuard(guard, guardContext)
      if (guardResult) {
        // Guard failed, return redirect response
        return guardResult
      }
    }

    // If no data function, just return null (guard passed, no data to load)
    if (!dataFn) {
      return null
    }

    // Execute data loading function
    const loaderContext: LoaderContext = {
      ...ctx,
      params: args.params,
      request: args.request
    }

    const data = dataFn(loaderContext)

    // Await the data to ensure it's loaded before rendering
    // This guarantees components always have data available
    const resolvedData = data instanceof Promise ? await data : data
    return { data: resolvedData }
  }
}

// ============================================
// IMAGE PRELOADING
// ============================================

/**
 * Preloads images by creating Image elements and waiting for them to load
 * Useful for ensuring images are cached before showing a page
 *
 * @param imagePaths - Array of image paths to preload
 * @returns Promise that resolves when all images are loaded
 *
 * @example
 * await preloadImages(
 *   '/images/banner.png',
 *   '/images/logo.svg'
 * )
 */
export async function preloadImages(...imagePaths: string[]): Promise<HTMLImageElement[]> {
  return Promise.all(
    imagePaths.map(
      path =>
        new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image()
          img.onload = () => resolve(img)
          img.onerror = reject
          img.src = path
        })
    )
  )
}

/**
 * Decodes and preloads an image from a dynamic import
 * Used with Vite's dynamic imports: import('./image.png')
 *
 * @param importImageModule - Promise from dynamic import
 * @returns Promise that resolves when image is decoded
 *
 * @example
 * await decodeImportImage(import('@/assets/images/banner.png'))
 */
export async function decodeImportImage(
  importImageModule: Promise<{ default: string }>
): Promise<HTMLImageElement> {
  return importImageModule.then(async module => {
    const img = new Image()
    img.src = module.default
    await img.decode()
    return img
  })
}

// ============================================
// REMOTE MODULE LOADING
// ============================================

/**
 * Dynamically imports a remote use case factory from the factories/usecases directory
 *
 * @param moduleName - Name of the factory function (e.g., 'makeRemoteAccountProfile')
 * @param queryClient - QueryClient instance to pass to the factory
 * @returns Promise resolving to the use case instance
 *
 * @example
 * const accountProfile = await importRemote<Account>(
 *   'makeRemoteAccountProfile',
 *   queryClient
 * )
 * const data = await accountProfile.loader()
 */
export async function importRemote<T>(moduleName: string, queryClient: QueryClient): Promise<T> {
  const module = await import('@/main/factories/usecases')
  const factory = module[moduleName as keyof typeof module]

  if (!factory || typeof factory !== 'function') {
    throw new Error(`Remote factory "${moduleName}" not found in usecases factories`)
  }

  return factory(queryClient) as T
}

// ============================================
// DEFER HELPERS
// ============================================

/**
 * Creates a deferred data object for React Router suspense
 * Use this when you want to show the page immediately and load data in the background
 *
 * @param data - Object with promises or values to defer
 * @returns Deferred data object
 *
 * @example
 * return deferData({
 *   user: fetchUser(),
 *   posts: fetchPosts()
 * })
 */
export function deferData<T extends Record<string, unknown>>(data: T) {
  return defer(data)
}

// ============================================
// LOADER FACTORY
// ============================================

/**
 * Creates a loader factory pre-initialized with context
 * This simplifies route definitions by eliminating manual context injection
 *
 * @param account - Authenticated user account
 * @param queryClient - React Query client for data caching/fetching
 * @param ldClient - LaunchDarkly client for feature flags (optional)
 * @returns Function that creates loaders: (guard, dataFn) => loader
 *
 * The returned function accepts:
 * - guard: Access validation (null = no validation, guards.xxx = specific validation)
 * - dataFn: Function that loads data (null = no data, function = loads data)
 *
 * dataFn receives LoaderContext with: { account, queryClient, ldClient, params, request }
 *
 * @example
 * // Create the factory once
 * const routeLoader = createLoaderFactory(account, queryClient, ldClient)
 *
 * // Use in multiple routes
 *
 * // Route without validation, without data
 * loader: routeLoader(null, null)
 *
 * // Route with validation, without data
 * loader: routeLoader(guards.companyManager, null)
 *
 * // Route without validation, with simple data
 * loader: routeLoader(null, () => loadHome(queryClient))
 *
 * // Route with validation and data
 * loader: routeLoader(guards.passPlanHolder, () => loadDashboard(queryClient))
 *
 * // Using URL params
 * loader: routeLoader(null, ({ params }) => loadDetail(params.id, queryClient))
 *
 * // Using query params from request
 * loader: routeLoader(null, ({ request }) => {
 *   const searchParams = new URL(request.url).searchParams
 *   return loadSearch(searchParams, queryClient)
 * })
 */
export function createLoaderFactory(account: Account.Model) {
  const baseContext = { account, queryClient, ldClient: FeatureFlagClient.getClient() }

  return (guard: Guard | null, dataFn: DataFunction | null) => {
    return (args: LoaderFunctionArgs) => {
      return createLoader(guard, dataFn)(args, baseContext)
    }
  }
}

// ============================================
// LOADER CONTEXT CREATION
// ============================================

/**
 * Creates a loader context object from router-factory dependencies
 * This is used by route builders to create the context passed to loaders
 *
 * @param account - Current user account
 * @param queryClient - React Query client
 * @param ldClient - LaunchDarkly client
 * @returns LoaderContext object
 */
export function createLoaderContext(
  account: Account.Model
): Omit<LoaderContext, 'params' | 'request'> {
  return {
    account,
    queryClient,
    ldClient: FeatureFlagClient.getClient()
  }
}

// ============================================
// CONTENT LOADING HELPERS
// ============================================

/**
 * Fetches content details for a given content ID
 * Used by enrollment and content detail loaders
 *
 * @param queryClient - React Query client
 * @param id - Content ID
 * @param toEnrollment - Whether this is for enrollment flow
 * @returns Promise resolving to content details
 */
export const contentDetailsPromise = async (
  queryClient: QueryClient,
  id: number,
  toEnrollment: boolean = false
) => {
  const module = await import('@remote/content/remote-user-content-details-factory')
  const make = module.makeRemoteUserContentDetails
  const remote = make(queryClient)
  return await remote.get(id, toEnrollment)
}

/**
 * Fetches enrollment content information for a given content ID
 * Used by enrollment flow loaders
 *
 * @param queryClient - React Query client
 * @param id - Content ID
 * @param toEnrollment - Whether this is for enrollment flow
 * @returns Promise resolving to enrollment content info
 */
export const enrollmentContentInfoPromise = async (
  queryClient: QueryClient,
  id: number,
  toEnrollment: boolean = false
) => {
  const module = await import(
    '@remote/content/enrollment/remote-user-enrollment-content-info-factory'
  )
  const make = module.makeRemoteUserEnrollmentContentInfo
  const remote = make(queryClient)
  return await remote.get(id, toEnrollment)
}
